// frontend/src/screens/admin/WardPreShiftCreateScreen.jsx
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Tabs, Tab, Container, Table, Modal } from 'react-bootstrap';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';

import { dublinLocalToUtcISO } from '../../utils/dublinTime';


import {
  CATEGORY_OF_STAFF,
  REASON_FOR_REQUEST,
  SHIFT_STATUS,
  LINE_STATUS,
  SPECIALS_REASON,       // ← ADD
} from '../../utils/enums';




import { useCreatePreShiftMutation } from '../../slices/preShiftApiSlice';
import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';



const SPECIALS_TRIGGER_REASONS = ['1-1 special', 'Cohorts'];


// Reasons that restrict shift creation to within 48 hours
const FORTY_EIGHT_HOUR_REASONS = ['1-1 special', 'Cohorts'];

// Shift time presets
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

// Recurrence options
const RECURRENCE_OPTIONS = {
  NONE: { label: 'No Recurrence', value: 'NONE' },
  DAILY: { label: 'Daily', value: 'DAILY' },
  WEEKLY: { label: 'Weekly', value: 'WEEKLY' },
  BIWEEKLY: { label: 'Every 2 Weeks', value: 'BIWEEKLY' },
  MONTHLY: { label: 'Monthly', value: 'MONTHLY' },
};

// Days of week for recurrence
const DAYS_OF_WEEK = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' },
];

// Categories that should only be selectable by admins (e.g. "ECT", "Bank HCA")
const isAdminOnlyCategory = (categoryValue, categoryLabel) => {
  const text = `${categoryValue || ''} ${categoryLabel || ''}`.trim().toLowerCase();
  return text.startsWith('ect') || text.startsWith('bank') || text.includes(' ect') || text.includes(' bank');
};

// Mirrors the backend's isNurseCategory check in preShiftController.js —
// used only to show/hide the auto-approval banner, the actual decision
// is always made server-side.
const isNurseCategoryClient = (categoryOfStaff) => {
  if (!categoryOfStaff) return false;
  return String(categoryOfStaff).trim().toLowerCase().includes('nurse');
};


const WardPreShiftCreateScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  // Check if user is manager
  const isManager = userInfo?.isManager === true;
  const managerDirectorate = userInfo?.directorateName || null;

  // Admin-configured ward area inclusions — additional wards a manager can
  // see/use regardless of their own directorate. Comes through on userInfo
  // from the login/verifyOTP response (populated HospitalLocation objects).
  const managerWardInclusions = userInfo?.wardAreaInclusions || [];
  const inclusionIds = useMemo(
    () =>
      new Set(
        managerWardInclusions.map((w) =>
          typeof w === 'object' && w !== null ? w._id : w
        )
      ),
    [managerWardInclusions]
  );

  // Form state
  const [hospitalLocationId, setHospitalLocationId] = useState('');
  const [wardDepartmentArea, setWardDepartmentArea] = useState('');
  const [reasonForRequest, setReasonForRequest] = useState('');
  const [reasonForRequestDetails, setReasonForRequestDetails] = useState('');
  const [categoryOfStaff, setCategoryOfStaff] = useState('');
  const [escort, setEscort] = useState(false);
  const [special, setSpecial] = useState(false);
  const [isOvertime, setIsOvertime] = useState(false);
  const [eccNumber, setEccNumber] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [comments, setComments] = useState('');
  const [numberOfShifts, setNumberOfShifts] = useState(1);
  const [specialsReason, setSpecialsReason] = useState('');



  // Replace the existing numberOfShifts state with this:
  const [shiftsPerDay, setShiftsPerDay] = useState({
    MON: 1,
    TUE: 1,
    WED: 1,
    THU: 1,
    FRI: 1,
    SAT: 1,
    SUN: 1
  });
  
  // Date and time
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const [selectedPreset, setSelectedPreset] = useState('AM');

  // Recurrence settings
  const [recurrenceType, setRecurrenceType] = useState('NONE');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceOccurrences, setRecurrenceOccurrences] = useState(1);
  //const [selectedDays, setSelectedDays] = useState(['MON', 'TUE', 'WED', 'THU', 'FRI']);
  const [selectedDays, setSelectedDays] = useState(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);

  // Modal state  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [shiftList, setShiftList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const {
    data: hospitalLocations,
    isLoading: loadingLocations,
    error: errorLocations,
  } = useGetHospitalLocationsQuery();

  const [createPreShift, { isLoading: loadingCreate }] = useCreatePreShiftMutation();

  // Filter hospital locations based on manager's directorate, PLUS any
  // admin-configured ward area inclusions (extra wards outside their own
  // directorate that the admin has explicitly granted them visibility on).
  //
  // NOTE: directorate strings in HospitalLocation are free-text and have
  // been observed with trailing/leading whitespace (e.g. "Medical 2
  // Directorate " vs "Medical 2 Directorate") due to manual admin edits.
  // Normalize both sides before comparing so a stray space doesn't silently
  // drop a manager's own directorate wards from their view.
  const normalizeDirectorate = (val) => (val || '').trim().toLowerCase();
  const normalizedManagerDirectorate = useMemo(
    () => normalizeDirectorate(managerDirectorate),
    [managerDirectorate]
  );

  const isNurseShiftApprover = userInfo?.isManager === true && userInfo?.isNurseShiftApprover === true;

  const willAutoApprove = useMemo(
    () => isNurseShiftApprover && isNurseCategoryClient(categoryOfStaff),
    [isNurseShiftApprover, categoryOfStaff]
  );


  const filteredHospitalLocations = useMemo(() => {
    if (!hospitalLocations || hospitalLocations.length === 0) return [];

    // If user is a manager with a directorate, filter by directorate OR inclusion list
    if (isManager && managerDirectorate) {
      console.log(`Filtering hospital locations for manager: ${userInfo?.name}`);
      console.log(`Manager directorate: "${managerDirectorate}"`);
      console.log(`Manager ward inclusions: ${inclusionIds.size}`);

      const filtered = hospitalLocations.filter(
        (location) =>
          normalizeDirectorate(location.directorate) === normalizedManagerDirectorate ||
          inclusionIds.has(location._id)
      );

      console.log(`Found ${filtered.length} locations in ${managerDirectorate} + inclusions`);
      return filtered;
    }

    // Manager with no directorate set, but inclusions configured — show only inclusions
    if (isManager && !managerDirectorate && inclusionIds.size > 0) {
      return hospitalLocations.filter((location) => inclusionIds.has(location._id));
    }

    // For non-managers (admins), show all locations
    return hospitalLocations;
  }, [hospitalLocations, isManager, managerDirectorate, normalizedManagerDirectorate, inclusionIds, userInfo]);

  // Auto-select first location if only one available (for managers with single location)
  useEffect(() => {
    if (filteredHospitalLocations.length === 1 && !hospitalLocationId) {
      const singleLocation = filteredHospitalLocations[0];
      setHospitalLocationId(singleLocation._id);
      setWardDepartmentArea(singleLocation.wardDepartmentArea || '');
    }
  }, [filteredHospitalLocations, hospitalLocationId]);


  // Filter out admin-only categories (ECT/Bank) for managers
const filteredCategoryOfStaff = useMemo(() => {
  if (!isManager) return CATEGORY_OF_STAFF; // admins see everything
  return CATEGORY_OF_STAFF.filter(
    (c) => !isAdminOnlyCategory(c.value, c.label)
  );
}, [isManager]);


  // Initialize dates when component mounts
  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);
    setEndDate(todayStr);
    
    // Set recurrence end date to the Sunday after the end date
    // For now, end date is today, so find the coming Sunday
    const sundayAfterEndDate = new Date(today);
    const daysUntilSunday = (7 - today.getDay()) % 7; // Get days until next Sunday (0 if today is Sunday)
    sundayAfterEndDate.setDate(today.getDate() + (daysUntilSunday === 0 ? 7 : daysUntilSunday));
    setRecurrenceEndDate(sundayAfterEndDate.toISOString().split('T')[0]);
  }, []);

  // Update recurrence end date to the Sunday after the selected end date
  useEffect(() => {
    if (endDate) {
      const endDateObj = new Date(endDate);
      const sundayAfterEndDate = new Date(endDateObj);
      const daysUntilSunday = (7 - endDateObj.getDay()) % 7; // Get days until next Sunday
      
      // If end date is Sunday, we want next Sunday (add 7 days)
      // If end date is any other day, add days until Sunday
      sundayAfterEndDate.setDate(endDateObj.getDate() + (daysUntilSunday === 0 ? 7 : daysUntilSunday));
      
      setRecurrenceEndDate(sundayAfterEndDate.toISOString().split('T')[0]);
    }
  }, [endDate]);

  // Update end date when start date changes
  useEffect(() => {
    if (startDate && !endDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  // Apply preset times when selected
  useEffect(() => {
    const preset = SHIFT_PRESETS[selectedPreset];
    if (preset) {
      setStartTime(preset.start);
      setEndTime(preset.end);
      
      // Update escort checkbox based on preset
      if (selectedPreset === 'ESCORT') {
        setEscort(true);
      }
    }
  }, [selectedPreset]);

  // Handle day selection for recurrence
  const handleDayToggle = (dayValue) => {
    if (selectedDays.includes(dayValue)) {
      setSelectedDays(selectedDays.filter(day => day !== dayValue));
    } else {
      setSelectedDays([...selectedDays, dayValue]);
    }
  };

  // Handle updating shifts per day for a specific day
  const handleShiftsPerDayChange = (day, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    setShiftsPerDay(prev => ({
      ...prev,
      [day]: numValue
    }));
  };


 const applyTimeRestriction = (shifts) => {
  const isRestricted = FORTY_EIGHT_HOUR_REASONS.some(
    (r) => reasonForRequest.toLowerCase() === r.toLowerCase()
  );
  if (!isRestricted) return shifts;

  const now = new Date();
  const isFriday = now.getDay() === 5;  // 0=Sun, 5=Fri, 6=Sat
  const windowHours = isFriday ? 72 : 48;
  const cutoff = new Date(now.getTime() + windowHours * 60 * 60 * 1000);

  return shifts.filter((shift) => {
    const shiftStart = new Date(`${shift.startDate}T${shift.startTime}`);
    return shiftStart <= cutoff;
  });
};


  // Generate all shift dates based on recurrence settings
  const generateShiftDates = () => {
    const shifts = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    console.log('=== GENERATE SHIFT DATES START ===');
    console.log('Input parameters:', {
      recurrenceType,
      selectedDays: [...selectedDays],
      shiftsPerDay,
      startDate,
      endDate,
      startTime,
      endTime,
      recurrenceEndDate,
      recurrenceOccurrences
    });
    
    // For NON-RECURRING shifts
    if (recurrenceType === 'NONE') {
      // For non-recurring, use Monday's shift count as default (or first selected day)
      const defaultShifts = shiftsPerDay.MON || 1;
      for (let i = 0; i < defaultShifts; i++) {
        shifts.push({
          id: `${startDate}-${startTime}-${endTime}-${i}-${Date.now()}`,
          startDate: startDate,
          endDate: endDate,
          startTime,
          endTime,
          shiftNumber: i + 1
        });
      }
      return shifts;
    }
    
    // Map day values for weekly recurrences
    const dayMap = {
      'SUN': 0, 'MON': 1, 'TUE': 2, 'WED': 3, 
      'THU': 4, 'FRI': 5, 'SAT': 6
    };
    
    // For recurrence, we need the day numbers for filtering
    let selectedDayNumbers;
    
    if (recurrenceType === 'DAILY' || recurrenceType === 'WEEKLY' || recurrenceType === 'BIWEEKLY') {
      // For daily/weekly recurrence, use selected days
      selectedDayNumbers = selectedDays.map(day => dayMap[day]);
    } else {
      // For MONTHLY recurrence, include all days
      selectedDayNumbers = [0, 1, 2, 3, 4, 5, 6];
    }
    
    console.log('Selected day numbers:', selectedDayNumbers);
    
    let totalOccurrences = 0;
    const maxOccurrences = parseInt(recurrenceOccurrences);
    const recurrenceEnd = recurrenceEndDate ? new Date(recurrenceEndDate) : null;
    
    console.log('Max occurrences:', maxOccurrences);
    console.log('Recurrence end date:', recurrenceEnd);
    
    // Helper to get the day key from date
    const getDayKeyFromDate = (date) => {
      const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      return days[date.getDay()];
    };
    
    // Helper to get number of shifts for a given date
    const getShiftsCountForDate = (date) => {
      const dayKey = getDayKeyFromDate(date);
      return shiftsPerDay[dayKey] || 0;
    };
    
    // DAILY recurrence with day filtering and per-day shift counts
    if (recurrenceType === 'DAILY') {
      console.log('Starting DAILY recurrence calculation with per-day shift counts...');
      let currentDate = new Date(start);
      
      while (true) {
        // Check stop conditions
        if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
          console.log(`Stopping: reached max occurrences (${maxOccurrences})`);
          break;
        }
        if (recurrenceEnd && currentDate > recurrenceEnd) {
          console.log('Stopping: passed recurrence end date');
          break;
        }
        
        const currentDayOfWeek = currentDate.getDay();
        const dayKey = getDayKeyFromDate(currentDate);
        const shiftsForThisDay = shiftsPerDay[dayKey] || 0;
        
        // Only create shifts if day is selected AND has shifts > 0
        if (selectedDayNumbers.includes(currentDayOfWeek) && shiftsForThisDay > 0) {
          const dateStr = currentDate.toISOString().split('T')[0];
          console.log(`Creating ${shiftsForThisDay} shift(s) for ${dateStr} (${dayKey})`);
          
          // Create the shifts for this day
          for (let i = 0; i < shiftsForThisDay; i++) {
            shifts.push({
              id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
              startDate: dateStr,
              endDate: dateStr,
              startTime,
              endTime,
              dayOfWeek: dayKey,
              shiftIndex: i + 1
            });
            totalOccurrences++;
            
            console.log(`  Created shift ${totalOccurrences}: ${dateStr} ${startTime}-${endTime}`);
            
            if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
              console.log(`  Stopping: reached max occurrences`);
              break;
            }
          }
        } else if (selectedDayNumbers.includes(currentDayOfWeek)) {
          console.log(`Skipping ${currentDate.toDateString()} - shifts per day set to 0`);
        } else {
          console.log(`Skipping ${currentDate.toDateString()} - not in selected days`);
        }
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
        
        // Safety limit
        if (totalOccurrences > 1000) {
          console.log('Safety limit: stopped after 1000 shifts');
          break;
        }
      }
    }
    
    // WEEKLY recurrence with per-day shift counts
    else if (recurrenceType === 'WEEKLY') {
      let currentWeekStart = new Date(start);
      let weekCount = 0;
      
      console.log('Starting WEEKLY recurrence calculation with per-day shift counts...');
      
      while (true) {
        // Check stop conditions
        if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
          console.log(`Stopping: reached max occurrences (${maxOccurrences})`);
          break;
        }
        if (recurrenceEnd && currentWeekStart > recurrenceEnd) {
          console.log('Stopping: passed recurrence end date');
          break;
        }
        
        console.log(`\nWeek ${weekCount + 1} (starting ${currentWeekStart.toDateString()}):`);
        
        // For each selected day in this week
        for (const dayNumber of selectedDayNumbers) {
          // Calculate date for this specific day
          const dayDate = new Date(currentWeekStart);
          const currentDayOfWeek = dayDate.getDay();
          const daysToAdd = (dayNumber - currentDayOfWeek + 7) % 7;
          dayDate.setDate(dayDate.getDate() + daysToAdd);
          
          // Skip if before overall start date
          if (dayDate < start) {
            console.log(`  Skipping ${dayDate.toDateString()}: before start date`);
            continue;
          }
          
          // Check if we should stop
          if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
          if (recurrenceEnd && dayDate > recurrenceEnd) {
            console.log(`  Stopping: ${dayDate.toDateString()} is after recurrence end date`);
            break;
          }
          
          const dayKey = getDayKeyFromDate(dayDate);
          const shiftsForThisDay = shiftsPerDay[dayKey] || 0;
          
          if (shiftsForThisDay > 0) {
            const dateStr = dayDate.toISOString().split('T')[0];
            console.log(`  Creating ${shiftsForThisDay} shift(s) for ${dateStr} (${dayKey})`);
            
            // Create the shifts for this day
            for (let i = 0; i < shiftsForThisDay; i++) {
              shifts.push({
                id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
                startDate: dateStr,
                endDate: dateStr,
                startTime,
                endTime,
                week: weekCount + 1,
                dayOfWeek: dayKey,
                shiftIndex: i + 1
              });
              totalOccurrences++;
              
              console.log(`    Created shift ${totalOccurrences}: ${dateStr} ${startTime}-${endTime}`);
              
              if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
                console.log(`    Stopping: reached max occurrences`);
                break;
              }
            }
          } else {
            console.log(`  Skipping ${dayDate.toDateString()} - shifts per day set to 0`);
          }
          
          if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
        }
        
        // Move to next week
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        weekCount++;
        
        // Safety limit
        if (weekCount > 100) {
          console.log('Safety limit: stopped after 100 weeks');
          break;
        }
      }
    }
    
    // BIWEEKLY recurrence with per-day shift counts
    else if (recurrenceType === 'BIWEEKLY') {
      let currentWeekStart = new Date(start);
      let weekCount = 0;
      
      console.log('Starting BIWEEKLY recurrence calculation with per-day shift counts...');
      
      while (true) {
        // Check stop conditions
        if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
          console.log(`Stopping: reached max occurrences (${maxOccurrences})`);
          break;
        }
        if (recurrenceEnd && currentWeekStart > recurrenceEnd) {
          console.log('Stopping: passed recurrence end date');
          break;
        }
        
        console.log(`\nWeek ${weekCount + 1} (starting ${currentWeekStart.toDateString()}):`);
        
        // Only process every 2 weeks
        if (weekCount % 2 === 0) {
          // For each selected day in this week
          for (const dayNumber of selectedDayNumbers) {
            // Calculate date for this specific day
            const dayDate = new Date(currentWeekStart);
            const currentDayOfWeek = dayDate.getDay();
            const daysToAdd = (dayNumber - currentDayOfWeek + 7) % 7;
            dayDate.setDate(dayDate.getDate() + daysToAdd);
            
            // Skip if before overall start date
            if (dayDate < start) {
              console.log(`  Skipping ${dayDate.toDateString()}: before start date`);
              continue;
            }
            
            // Check if we should stop
            if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
            if (recurrenceEnd && dayDate > recurrenceEnd) {
              console.log(`  Stopping: ${dayDate.toDateString()} is after recurrence end date`);
              break;
            }
            
            const dayKey = getDayKeyFromDate(dayDate);
            const shiftsForThisDay = shiftsPerDay[dayKey] || 0;
            
            if (shiftsForThisDay > 0) {
              const dateStr = dayDate.toISOString().split('T')[0];
              console.log(`  Creating ${shiftsForThisDay} shift(s) for ${dateStr} (${dayKey})`);
              
              // Create the shifts for this day
              for (let i = 0; i < shiftsForThisDay; i++) {
                shifts.push({
                  id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
                  startDate: dateStr,
                  endDate: dateStr,
                  startTime,
                  endTime,
                  week: Math.floor(weekCount / 2) + 1,
                  dayOfWeek: dayKey,
                  shiftIndex: i + 1
                });
                totalOccurrences++;
                
                console.log(`    Created shift ${totalOccurrences}: ${dateStr} ${startTime}-${endTime}`);
                
                if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
                  console.log(`    Stopping: reached max occurrences`);
                  break;
                }
              }
            } else {
              console.log(`  Skipping ${dayDate.toDateString()} - shifts per day set to 0`);
            }
            
            if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
          }
        }
        
        // Move to next week
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        weekCount++;
        
        // Safety limit
        if (weekCount > 200) {
          console.log('Safety limit: stopped after 200 weeks');
          break;
        }
      }
    }
    
    // MONTHLY recurrence with per-day shift counts
    else if (recurrenceType === 'MONTHLY') {
      console.log('Starting MONTHLY recurrence calculation with per-day shift counts...');
      let currentDate = new Date(start);
      let monthCount = 0;
      
      while (true) {
        // Check stop conditions
        if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
          console.log(`Stopping: reached max occurrences (${maxOccurrences})`);
          break;
        }
        if (recurrenceEnd && currentDate > recurrenceEnd) {
          console.log('Stopping: passed recurrence end date');
          break;
        }
        
        const dayKey = getDayKeyFromDate(currentDate);
        const shiftsForThisDay = shiftsPerDay[dayKey] || 0;
        
        if (shiftsForThisDay > 0) {
          const dateStr = currentDate.toISOString().split('T')[0];
          console.log(`Creating ${shiftsForThisDay} shift(s) for ${dateStr} (${dayKey})`);
          
          // Create the shifts for this day
          for (let i = 0; i < shiftsForThisDay; i++) {
            shifts.push({
              id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
              startDate: dateStr,
              endDate: dateStr,
              startTime,
              endTime,
              monthIndex: monthCount + 1,
              dayOfWeek: dayKey,
              shiftIndex: i + 1
            });
            totalOccurrences++;
            
            console.log(`  Created shift ${totalOccurrences}: ${dateStr} ${startTime}-${endTime}`);
            
            if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) {
              console.log(`  Stopping: reached max occurrences`);
              break;
            }
          }
        } else {
          console.log(`Skipping ${currentDate.toDateString()} - shifts per day set to 0`);
        }
        
        // Move to next month (same day of month)
        currentDate.setMonth(currentDate.getMonth() + 1);
        monthCount++;
        
        // Safety limit
        if (monthCount > 60) {
          console.log('Safety limit: stopped after 60 months');
          break;
        }
      }
    }
    
    console.log(`\n=== FINAL RESULT ===`);
    console.log(`Total shifts generated: ${shifts.length}`);
    console.log('All shifts:', shifts.map((s, idx) => `${idx + 1}. ${s.startDate} ${s.startTime}-${s.endTime} (${s.dayOfWeek})`));
    console.log('=== GENERATE SHIFT DATES END ===\n');
    
    return shifts;
  };

  // Calculate total shifts that will be created
  
  const calculateTotalShifts = () => {
  const shiftDates = applyTimeRestriction(generateShiftDates());
  return shiftDates.length;
};


  // Sort shifts by date
  const sortShiftsByDate = (shifts) => {
    return [...shifts].sort((a, b) => {
      const dateCompare = a.startDate.localeCompare(b.startDate);
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    });
  };

  // Prepare shifts for confirmation
  const prepareShiftsForConfirmation = () => {
    //const shifts = generateShiftDates();

const rawShifts = generateShiftDates();
  const filtered = applyTimeRestriction(rawShifts);

  if (filtered.length < rawShifts.length) {
  const dropped = rawShifts.length - filtered.length;
  const windowHours = new Date().getDay() === 5 ? 72 : 48;
  toast.warning(
    `${dropped} shift${dropped > 1 ? 's' : ''} removed — "${reasonForRequest}" requests are limited to the next ${windowHours} hours${new Date().getDay() === 5 ? ' (Friday extension applied)' : ''}.`
  );
}


    
    const shiftsWithIds = filtered.map((shift, index) => ({
    ...shift,
    id: shift.id || `${shift.startDate}-${shift.startTime}-${shift.endTime}-${index}-${Date.now()}`,
  }));
    
    const sortedShifts = sortShiftsByDate(shiftsWithIds);
    setShiftList(sortedShifts);
    setShowConfirmation(true);
  };

  // Validate form before submission
  const validateForm = () => {
    const requiredFields = [
      { value: hospitalLocationId, message: 'Please select a hospital location' },
      { value: categoryOfStaff, message: 'Please select category of staff' },
      { value: reasonForRequest, message: 'Please select reason for request' },
      { value: startDate, message: 'Please select start date' },
      { value: reasonForRequestDetails, message: 'Please provide reason details' },
    ];

    // Validate specialsReason is filled when required
  if (['1-1 special', 'Cohorts'].some(r => reasonForRequest.toLowerCase() === r.toLowerCase())
      && !specialsReason) {
    toast.error('Please select a Specials / Cohorts reason');
    return;
  }

    
    for (const field of requiredFields) {
      if (!field.value) {
        toast.error(field.message);
        return false;
      }
    }
    
    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      toast.error('End date cannot be before start date');
      return false;
    }
    
    // Validate time
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    
    // Check if it's a valid overnight shift
    const isOvernight = startTime > endTime && startDate === endDate;
    
    if (!isOvernight && endDateTime <= startDateTime) {
      toast.error('End time must be after start time');
      return false;
    }
    
    const totalShifts = calculateTotalShifts();
    if (totalShifts > 50) {
      toast.error(`Cannot create more than 50 shift requests at once. You have ${totalShifts} shifts.`);
      return false;
    }
    
    return true;
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      toast.warning('Please wait, form is already submitting...');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    // Show confirmation modal with all shifts
    prepareShiftsForConfirmation();
  };

  // Handle removing a shift from the list
  const handleRemoveShift = (index) => {
    const updatedShifts = [...shiftList];
    updatedShifts.splice(index, 1);
    setShiftList(updatedShifts);
    toast.info(`Shift removed. ${updatedShifts.length} shifts remaining.`);
  };

  // Handle shift update (for editing in modal)
  const handleShiftUpdate = (index, field, value) => {
    const updatedShifts = [...shiftList];
    
    updatedShifts[index] = {
      ...updatedShifts[index],
      [field]: value
    };
    
    // Handle overnight shifts
    if (field === 'startTime' || field === 'endTime') {
      const shift = updatedShifts[index];
      if (shift.startTime > shift.endTime && shift.startDate === shift.endDate) {
        // This is an overnight shift - keep as is
      }
    }
    
    const sortedShifts = sortShiftsByDate(updatedShifts);
    setShiftList(sortedShifts);
  };

  // Calculate duration for display
  const calculateDuration = (startDate, startTime, endDate, endTime) => {
    const start = new Date(`${startDate}T${startTime}`);
    let end = new Date(`${endDate}T${endTime}`);
    
    if (end < start && startDate === endDate) {
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() + 1);
      end = adjustedEnd;
    }
    
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours === 0 && minutes === 0) return '0h';
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Get day of week
  const getDayOfWeek = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  // Check if shift is overnight
  const isOvernightShift = (shift) => {
    return shift.startTime > shift.endTime && shift.startDate === shift.endDate;
  };

  // Check if shift is valid
  const isShiftValid = (shift) => {
    const start = new Date(`${shift.startDate}T${shift.startTime}`);
    const end = new Date(`${shift.endDate}T${shift.endTime}`);
    
    if (end < start && shift.startDate === shift.endDate) {
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() + 1);
      return adjustedEnd > start;
    }
    
    return end > start;
  };

  // Create all pre-shift requests
  const createAllPreShifts = async () => {
    setIsSubmitting(true);
    
    try {
      const successfulShifts = [];
      const failedShifts = [];
      
      for (let i = 0; i < shiftList.length; i++) {
        const shift = shiftList[i];
        
        // Handle overnight shifts
        let endDateForShift = shift.endDate;
        const startDateTime = new Date(`${shift.startDate}T${shift.startTime}`);
        const endDateTime = new Date(`${shift.endDate}T${shift.endTime}`);
        
        if (endDateTime < startDateTime && shift.startDate === shift.endDate) {
          const tempDate = new Date(shift.endDate);
          tempDate.setDate(tempDate.getDate() + 1);
          endDateForShift = tempDate.toISOString().split('T')[0];
        }

        const shiftData = {
          startDateTime: dublinLocalToUtcISO(shift.startDate, shift.startTime),
          endDateTime: dublinLocalToUtcISO(endDateForShift, shift.endTime),
          hospitalLocationId,
          wardDepartmentArea,
          reasonForRequest,
          reasonForRequestDetails,
          specialsReason,          // ← ADD THIS
          categoryOfStaff,
          escort,
          special,
          isOvertime,
          eccNumber: eccNumber || undefined,
          specialRequirements,
          comments,
        };
        
        try {
          const result = await createPreShift(shiftData).unwrap();
          successfulShifts.push(result);
          console.log(`Created pre-shift ${i + 1}/${shiftList.length}`);
        } catch (err) {
          console.error(`Failed to create pre-shift ${i + 1}:`, err);
          failedShifts.push({ index: i + 1, error: err });
        }
        
        // Small delay between requests
        if (i < shiftList.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (successfulShifts.length > 0) {
        toast.success(`Successfully created ${successfulShifts.length} pre-shift request${successfulShifts.length > 1 ? 's' : ''}!`);
        
        if (failedShifts.length > 0) {
          toast.warning(`${failedShifts.length} request${failedShifts.length > 1 ? 's' : ''} failed to create.`);
        }
        
        setTimeout(() => {
          setShowConfirmation(false);
          navigate('/admin/ward-preshifts');
        }, 2000);
      } else {
        toast.error('Failed to create any pre-shift requests. Please try again.');
        setIsSubmitting(false);
      }
      
    } catch (err) {
      console.error('Error in createAllPreShifts:', err);
      toast.error(err?.data?.message || err.error || 'Failed to create pre-shift requests');
      setIsSubmitting(false);
    }
  };

  // Show warning if no locations in manager's directorate AND no inclusions configured
  const showNoLocationsWarning =
    isManager && managerDirectorate && filteredHospitalLocations.length === 0;

  return (
    <>
      <Container fluid="lg">
        {loadingCreate && <Loader />}
        {loadingLocations ? (
          <Loader />
        ) : errorLocations ? (
          <Message variant="danger">
            {errorLocations?.data?.message || errorLocations.error}
          </Message>
        ) : showNoLocationsWarning ? (
          <Message variant="warning">
            <strong>No Hospital Locations Found</strong>
            <p className="mb-0 mt-2">
              You are a manager for the <strong>{managerDirectorate}</strong> directorate, but there are no hospital locations assigned to this directorate, and no additional ward inclusions have been configured for your account.
              Please contact an administrator to assign locations to your directorate before creating pre-shift requests.
            </p>
          </Message>
        ) : (
          <Form onSubmit={submitHandler}>
            <Card className="mb-4 custom-card-bg">
              <Card.Header className="fw-bold fs-4">
                Create Pre-Shift Request{calculateTotalShifts() > 1 ? `s (${calculateTotalShifts()} total)` : ''}
                {isManager && managerDirectorate && (
                  <span className="ms-3 fs-6 text-primary">
                    Directorate: {managerDirectorate}
                  </span>
                )}
                {isManager && inclusionIds.size > 0 && (
                  <span className="ms-3 fs-6 text-success">
                    + {inclusionIds.size} additional ward{inclusionIds.size > 1 ? 's' : ''} included
                  </span>
                )}
              </Card.Header>

               {isNurseShiftApprover && (
                <div className={`alert ${willAutoApprove ? 'alert-success' : 'alert-info'} mb-0 rounded-0 border-0 d-flex align-items-center`}>
                  <i className="bi bi-lightning-charge-fill fs-4 me-3"></i>
                  <div>
                    <strong>Nurse Shift Approver</strong>
                    <p className="mb-0 mt-1">
                      {willAutoApprove ? (
                        <>
                          This request will be <strong>automatically approved</strong> and a live shift
                          created immediately — no admin sign-off needed for nurse categories.
                        </>
                      ) : (
                        <>
                          Your nurse-category requests are auto-approved and created immediately.
                          {categoryOfStaff
                            ? ' This request uses a non-nurse category, so it will still go through normal admin approval.'
                            : ' Select a category of staff to see whether this request qualifies.'}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              
              <Card.Body>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-4"
                >
                  <Tab eventKey="basic" title="Basic Information">
                    {/* Top Row: Hospital Location, Category, Reason */}
                    <Row className="mb-4">
                      <Col lg={4}>
                        <Form.Group controlId="isOvertime" className="mb-3">
                          <Form.Check
                            type="checkbox"
                            label={<span className="fw-bold">Overtime Shift</span>}
                            checked={isOvertime}
                            onChange={(e) => setIsOvertime(e.target.checked)}
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={4}>
                        <Form.Group controlId="hospitalLocationId">
                          <Form.Label className="fw-bold">Hospital Location</Form.Label>
                          <Form.Select
                            value={hospitalLocationId}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              setHospitalLocationId(selectedId);
                              const selectedLocation = hospitalLocations.find(
                                (loc) => loc._id === selectedId
                              );
                              setWardDepartmentArea(selectedLocation?.wardDepartmentArea || '');
                            }}
                            required
                            size="lg"
                          >
                            <option value="">Select Hospital Location...</option>
                            {filteredHospitalLocations.map((location) => {
                              const isInclusion =
                                isManager &&
                                managerDirectorate &&
                                normalizeDirectorate(location.directorate) !== normalizedManagerDirectorate &&
                                inclusionIds.has(location._id);
                              return (
                                <option key={location._id} value={location._id}>
                                  {location.wardDepartmentArea}
                                  {isManager && location.directorate && ` (${location.directorate})`}
                                  {isInclusion && ' ★ included'}
                                </option>
                              );
                            })}
                          </Form.Select>
                          {isManager && filteredHospitalLocations.length > 0 && (
                            <Form.Text className="text-muted">
                              Showing {filteredHospitalLocations.length} location(s)
                              {managerDirectorate && ` in your directorate`}
                              {inclusionIds.size > 0 && ` plus ${inclusionIds.size} included ward${inclusionIds.size > 1 ? 's' : ''} (★)`}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      <Col lg={4}>
                        <Form.Group controlId="categoryOfStaff">
                        <Form.Label className="fw-bold">Category of Staff</Form.Label>
                        <Form.Select
                          value={categoryOfStaff}
                          onChange={(e) => setCategoryOfStaff(e.target.value)}
                          required
                          size="lg"
                        >
                          <option value="">Select Category...</option>
                          {filteredCategoryOfStaff.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      </Col>
                    </Row>

                    {/* Reason for Request */}
                    <Row className="mb-4">
                      <Col lg={4}>
                        <Form.Group controlId="reasonForRequest">
                          <Form.Label className="fw-bold">Reason for Request</Form.Label>
                          <Form.Select
                            value={reasonForRequest}
                            onChange={(e) => {
                              setReasonForRequest(e.target.value);
                              setSpecialsReason('');
                                }}
                            required
                            size="lg"
                          >
                            <option value="">Select Reason...</option>
                            {REASON_FOR_REQUEST.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={8}>
                        <Form.Group controlId="reasonForRequestDetails">
                          <Form.Label className="fw-bold">Reason for Request Details</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={reasonForRequestDetails}
                            onChange={(e) => setReasonForRequestDetails(e.target.value)}
                            placeholder="Provide details about why this shift is needed..."
                            required
                          />
                        </Form.Group>
                      </Col>


                      {/* Specials/Cohorts sub-reason — only shown for '1-1 special' or 'Cohorts' */}
                            {SPECIALS_TRIGGER_REASONS.some(
                              (r) => reasonForRequest.toLowerCase() === r.toLowerCase()
                            ) && (
                              <Col lg={4}>
                                <Form.Group controlId="specialsReason">
                                  <Form.Label className="fw-bold">
                                    Specials / Cohorts Reason <span className="text-danger">*</span>
                                  </Form.Label>
                                  <Form.Select
                                    value={specialsReason}
                                    onChange={(e) => setSpecialsReason(e.target.value)}
                                    required
                                    size="lg"
                                  >
                                    <option value="">Select reason...</option>
                                    {SPECIALS_REASON.map((r) => (
                                      <option key={r.value} value={r.value}>
                                        {r.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  <Form.Text className="text-muted">
                                    Required for 1-1 Special and Cohort requests
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                            )}


                    </Row>

                    {/* Options Row */}
                    <Row className="mb-4">
                      <Col lg={6}>
                        <div className="border rounded p-3 bg-light">
                          <Form.Label className="fw-bold d-block">Options</Form.Label>
                          <Row>
                            <Col lg={4}>
                              <Form.Group controlId="escort" className="mb-3">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Escort Required</span>}
                                  checked={escort}
                                  onChange={(e) => setEscort(e.target.checked)}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={4}>
                              <Form.Group controlId="special">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Special</span>}
                                  checked={special}
                                  onChange={(e) => setSpecial(e.target.checked)}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={4}>
                              <Form.Group controlId="eccNumber">
                                <Form.Label className="fw-bold">ECC Number</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={eccNumber}
                                  onChange={(e) => setEccNumber(e.target.value)}
                                  placeholder="Enter ECC number..."
                                  size="sm"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="border rounded p-3 bg-light">
                          <Form.Label className="fw-bold d-block">Requestor Information</Form.Label>
                          <Row>
                            <Col lg={6}>
                              <Form.Group controlId="requestorName">
                                <Form.Label className="fw-bold">Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={userInfo?.name || ''}
                                  readOnly
                                  disabled
                                  size="sm"
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={6}>
                              <Form.Group controlId="requestorEmail">
                                <Form.Label className="fw-bold">Email</Form.Label>
                                <Form.Control
                                  type="email"
                                  value={userInfo?.email || ''}
                                  readOnly
                                  disabled
                                  size="sm"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>

                    {/* Special Requirements and Comments */}
                    <Row className="mb-4">
                      <Col lg={6}>
                        <Form.Group controlId="specialRequirements">
                          <Form.Label className="fw-bold">Special Requirements</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={specialRequirements}
                            onChange={(e) => setSpecialRequirements(e.target.value)}
                            placeholder="Any special equipment, training, or requirements..."
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={6}>
                        <Form.Group controlId="comments">
                          <Form.Label className="fw-bold">Comments</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Additional notes or comments..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab>

                  <Tab eventKey="schedule" title="Schedule">
                    <Row className="mb-4">
                      <Col lg={12}  className="border rounded p-3 bg-info bg-opacity-10">
                        <Form.Group controlId="shiftPreset">
                          <Form.Label className="fw-bold">Shift Time Preset</Form.Label>
                          <Row className="g-2">
                            {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                              <Col lg={3} md={4} sm={6} key={key}>
                                <div 
                                  className={`border rounded p-2 cursor-pointer ${selectedPreset === key ? 'bg-primary text-white' : 'bg-light'}`}
                                  onClick={() => setSelectedPreset(key)}
                                  style={{cursor: 'pointer'}}
                                >
                                  <Form.Check
                                    type="radio"
                                    id={`preset-${key}`}
                                    label={preset.label}
                                    name="shiftPreset"
                                    checked={selectedPreset === key}
                                    onChange={() => {}}
                                    className="mb-0"
                                  />
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col lg={12} className="border rounded p-3 bg-info bg-opacity-10">
                        <Row className="g-3">
                          <Col md={3}>
                            <Form.Group controlId="startDate">
                              <Form.Label className="fw-bold">Start Date</Form.Label>
                              <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                                size="lg"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={3}>
                            <Form.Group controlId="endDate">
                              <Form.Label className="fw-bold">End Date</Form.Label>
                              <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                min={startDate}
                                required
                                size="lg"
                              />
                              <Form.Text className="text-muted">
                                Same day unless overnight shift
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          <Col md={3}>
                            <Form.Group controlId="startTime">
                              <Form.Label className="fw-bold">Start Time</Form.Label>
                              <Form.Control
                                type="time"
                                value={startTime}  
                                onChange={(e) => setStartTime(e.target.value)}  
                                required
                                size="lg"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={3}>
                            <Form.Group controlId="endTime">
                              <Form.Label className="fw-bold">End Time</Form.Label>
                              <Form.Control
                                type="time"
                                value={endTime} 
                                onChange={(e) => setEndTime(e.target.value)}  
                                required
                                size="lg"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col lg={12} className="border rounded p-3 bg-info bg-opacity-10">
                        <Form.Group controlId="recurrenceType">
                          <Form.Label className="fw-bold">Recurrence Pattern</Form.Label>
                          
                          <Row className="g-2 flex-nowrap">
                            {Object.values(RECURRENCE_OPTIONS).map((option) => (
                              <Col key={option.value}>
                                <div 
                                  className={`border rounded p-2 cursor-pointer ${recurrenceType === option.value ? 'bg-primary text-white' : 'bg-light'}`}
                                  onClick={() => setRecurrenceType(option.value)}
                                  style={{cursor: 'pointer'}}
                                >
                                  <Form.Check
                                    type="radio"
                                    id={`recurrence-${option.value}`}
                                    label={option.label}
                                    name="recurrenceType"
                                    checked={recurrenceType === option.value}
                                    onChange={() => {}}
                                    className="mb-0 text-nowrap"
                                  />
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Form.Group>
                      </Col>
                    </Row>

                    {recurrenceType !== 'NONE' && (
                      <>
                        <Row className="mb-4">
                          <Col lg={12} className="border rounded p-3 bg-info bg-opacity-10">
                            <Row className="g-3">
                              <Col md={6}>
                                <Form.Group controlId="recurrenceEndDate">
                                  <Form.Label className="fw-bold">Recurrence End Date</Form.Label>
                                  <Form.Control
                                    type="date"
                                    value={recurrenceEndDate}
                                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                                    min={startDate}
                                    size="lg"
                                  />
                                  <Form.Text className="text-muted">
                                    Leave empty for no end date
                                  </Form.Text>
                                </Form.Group>
                              </Col>

                              <Col md={6}>
                                <Form.Group controlId="recurrenceOccurrences">
                                  <Form.Label className="fw-bold">Number of Occurrences</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={recurrenceOccurrences}
                                    onChange={(e) => setRecurrenceOccurrences(Math.max(1, parseInt(e.target.value) || 1))}
                                    size="lg"
                                  />
                                  <Form.Text className="text-muted">
                                    Leave as 1 for unlimited (until end date)
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                            </Row>
                          </Col>
                        </Row>

                        <Row className="mb-4">
                          <Col lg={12}>
                            <Form.Label className="fw-bold d-block mb-2">Repeat On Days & Shifts per Day</Form.Label>
                            <div className="d-flex flex-wrap gap-3 border rounded p-3 bg-light">
                              {DAYS_OF_WEEK.map((day) => (
                                <div key={day.value} className="d-flex align-items-center" style={{minWidth: '180px'}}>
                                  <Form.Check
                                    type="checkbox"
                                    id={`day-${day.value}`}
                                    label={<span className="fw-bold me-2">{day.label}</span>}
                                    checked={selectedDays.includes(day.value)}
                                    onChange={() => handleDayToggle(day.value)}
                                    disabled={recurrenceType === 'NONE'}
                                    className="me-2"
                                  />
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={shiftsPerDay[day.value]}
                                    onChange={(e) => handleShiftsPerDayChange(day.value, e.target.value)}
                                    size="sm"
                                    disabled={recurrenceType === 'NONE' || !selectedDays.includes(day.value)}
                                    className={!selectedDays.includes(day.value) && recurrenceType !== 'NONE' ? 'bg-light text-muted' : ''}
                                    style={{ width: '70px' }}
                                  />
                                </div>
                              ))}
                            </div>
                            <div className="d-flex justify-content-between mt-2">
                              <Form.Text className="text-muted">
                                Select days and set number of shifts (0 to skip)
                              </Form.Text>
                              {recurrenceType !== 'NONE' && (
                                <Form.Text className="text-primary fw-bold">
                                  Total weekly: {DAYS_OF_WEEK.reduce((total, day) => 
                                    total + (selectedDays.includes(day.value) ? (shiftsPerDay[day.value] || 0) : 0), 0
                                  )} shifts
                                </Form.Text>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </>
                    )}

                   {FORTY_EIGHT_HOUR_REASONS.some(
                        (r) => reasonForRequest.toLowerCase() === r.toLowerCase()
                      ) && (
                        <Row className="mb-4">
                          <Col lg={12}>
                            <div className="alert alert-warning d-flex align-items-center">
                              <i className="bi bi-clock-history fs-4 me-3"></i>
                              <div>
                                <strong>
                                  {new Date().getDay() === 5 ? '72' : '48'}-Hour Limit Applied
                                </strong>
                                <p className="mb-0 mt-1">
                                  Requests for <strong>{reasonForRequest}</strong> can only be
                                  created for shifts starting within the next{' '}
                                  <strong>{new Date().getDay() === 5 ? '72' : '48'} hours</strong>
                                  {new Date().getDay() === 5 && (
                                    <span className="ms-1">(extended to 72 hours as today is Friday)</span>
                                  )}.
                                  Any shifts beyond that window will be automatically excluded.
                                </p>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}



                    <Row className="mb-4">
                      <Col lg={12}>
                        <div className="alert alert-primary">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <i className="bi bi-info-circle fs-4"></i>
                            </div>
                            <div>
                              <strong className="fs-5">Summary:</strong>
                              <div className="mt-1">
                                This will create <strong className="fs-5">{calculateTotalShifts()}</strong> pre-shift request{calculateTotalShifts() > 1 ? 's' : ''}
                                {recurrenceType !== 'NONE' && <span> with <strong>{recurrenceType.toLowerCase()}</strong> recurrence</span>}
                                {selectedDays.length > 0 && recurrenceType !== 'NONE' && <span> on <strong>{selectedDays.length}</strong> selected days</span>}
                                {numberOfShifts > 1 && <span>, with <strong>{numberOfShifts}</strong> shift{numberOfShifts > 1 ? 's' : ''} per day</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Tab>
                </Tabs>

                <div className="d-flex justify-content-between pt-3 border-top">
                  <Button 
                    variant="secondary" 
                    onClick={() => navigate('/admin/ward-preshifts')}
                    size="lg"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"  
                    disabled={isSubmitting || loadingCreate || filteredHospitalLocations.length === 0}
                  >
                    {isSubmitting ? 'Processing...' : `Review ${calculateTotalShifts() > 1 ? `${calculateTotalShifts()} Requests` : 'Request'}`}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Form>
        )}

        {/* Confirmation Modal */}
        <Modal 
          show={showConfirmation} 
          onHide={() => !isSubmitting && setShowConfirmation(false)}
          size="xl"
          backdrop="static"
          keyboard={!isSubmitting}
        >
          <Modal.Header closeButton={!isSubmitting}>
            <Modal.Title>
              Confirm Pre-Shift Requests ({shiftList.length} Requests)
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-info mb-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <i className="bi bi-info-circle fs-4"></i>
                </div>
                <div>
                  <strong>Review Your Requests</strong>
                  <p className="mb-0 mt-1">
                    You are about to submit {shiftList.length} pre-shift request{shiftList.length > 1 ? 's' : ''} for approval.
                    Each request will need to be approved by an administrator.
                  </p>
                </div>
              </div>
            </div>

            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Duration</th>
                    <th>Type</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {shiftList.map((shift, index) => {
                    const isValid = isShiftValid(shift);
                    const isOvernight = isOvernightShift(shift);
                    
                    return (
                      <tr key={shift.id} className={!isValid ? 'table-warning' : ''}>
                        <td className="fw-bold">{index + 1}</td>
                        <td>
                          <Form.Control
                            type="date"
                            value={shift.startDate}
                            onChange={(e) => handleShiftUpdate(index, 'startDate', e.target.value)}
                            size="sm"
                            disabled={isSubmitting}
                          />
                        </td>
                        <td className="fw-bold">{getDayOfWeek(shift.startDate)}</td>
                        <td>
                          <Form.Control
                            type="time"
                            value={shift.startTime}
                            onChange={(e) => handleShiftUpdate(index, 'startTime', e.target.value)}
                            size="sm"
                            disabled={isSubmitting}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="time"
                            value={shift.endTime}
                            onChange={(e) => handleShiftUpdate(index, 'endTime', e.target.value)}
                            size="sm"
                            disabled={isSubmitting}
                          />
                        </td>
                        <td className="fw-bold">
                          {calculateDuration(shift.startDate, shift.startTime, shift.endDate, shift.endTime)}
                          {isOvernight && <span className="ms-1 text-primary">🌙</span>}
                        </td>
                        <td>
                          {!isValid ? (
                            <span className="badge bg-warning text-dark">Invalid</span>
                          ) : isOvernight ? (
                            <span className="badge bg-info">Overnight</span>
                          ) : (
                            <span className="badge bg-success">Valid</span>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveShift(index)}
                            disabled={isSubmitting}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <div className="mt-4">
              <Row>
                <Col md={6}>
                  <div className="border rounded p-3 bg-light">
                    <h6>Request Summary</h6>
                    <ul className="mb-0">
                      <li>Total Requests: <strong>{shiftList.length}</strong></li>
                      <li>Overnight Shifts: <strong>{shiftList.filter(isOvernightShift).length}</strong></li>
                      <li>Invalid Shifts: <strong>{shiftList.filter(s => !isShiftValid(s)).length}</strong></li>
                    </ul>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="border rounded p-3 bg-light">
                    <h6>Location & Category</h6>
                    <ul className="mb-0">
                      <li><strong>Location:</strong> {hospitalLocations?.find(l => l._id === hospitalLocationId)?.wardDepartmentArea}</li>
                      <li><strong>Category:</strong> {categoryOfStaff}</li>
                      <li><strong>Reason:</strong> {reasonForRequest}</li>
                    </ul>
                  </div>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowConfirmation(false)}
              disabled={isSubmitting}
            >
              Back to Edit
            </Button>
            <Button 
              variant="primary" 
              onClick={createAllPreShifts}
              disabled={isSubmitting || shiftList.length === 0 || shiftList.some(s => !isShiftValid(s))}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Creating {shiftList.length} Requests...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  Submit {shiftList.length} Request{shiftList.length > 1 ? 's' : ''} for Approval
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default WardPreShiftCreateScreen;
